import { atom, useAtomValue } from 'jotai';
import { LanguageContext } from 'lib-knowledge-management-common/contexts/language';
import {
  ArticleSearchInput,
  ArticlesQueryVariables,
  InputMaybe,
  LanguageId,
  PublicationStatus,
} from 'lib-knowledge-management-common/graphql';
import { filterNonNullStrings } from 'lib-knowledge-utils/array';
import { enumToPlainObject } from 'lib-knowledge-utils/object';
import { useContext, useMemo } from 'react';
import { ArrayParam, StringParam, useQueryParams } from 'use-query-params';

import {
  AppFilterStore,
  getArticleFilters,
  PublicationStatusKey,
} from './useArticlesFiltersUtils';

export const offsetAtom = atom(0);
export const limitAtom = atom(32);

export const mappedPublicationStatuses = enumToPlainObject(PublicationStatus);
/**
 * This is a special id that is used to indicate that we want to include orphans
 * in the collection.
 *
 * A truthy name was chosen for this ID as the dropdown label is "No collection"
 *
 * NoCollections === !excludeOrphans !noCollections === excludeOrphans
 */
export const noCollectionsId = 'noCollections';

type AppFilterActions = {
  setSearchInput: (value: string) => void;
  setPublicationStatuses: (value: string[]) => void;
  setOwners: (value: string[]) => void;
  setCollections: (value: string[]) => void;
  setLanguageId: (languageId: string[]) => void;
};

const articlesFiltersParams = {
  search: StringParam,
  publicationStatus: ArrayParam,
  collectionId: ArrayParam,
  ownerId: ArrayParam,
  excludeOrphans: StringParam,
  langIdFilter: ArrayParam,
};
export function useArticlesFilters(): {
  store: AppFilterStore;
  actions: AppFilterActions;
  articlesQueryVariables: ArticlesQueryVariables;
} {
  const [untypedStore, setFilters] = useQueryParams(articlesFiltersParams);

  const offset = useAtomValue(offsetAtom); // TODO - we might be able to do useState instead
  const limit = useAtomValue(limitAtom); // TODO - we might be able to do useState instead

  const {
    selectedLanguage,
    defaultLanguage: { languageId: defaultLanguageId },
  } = useContext(LanguageContext);

  const actions: AppFilterActions = useMemo(() => {
    function setSearchInput(searchValue: string) {
      searchValue.length
        ? setFilters({ search: searchValue })
        : setFilters({ search: undefined });
    }
    function setPublicationStatuses(statuses: string[]) {
      statuses.length
        ? setFilters({ publicationStatus: statuses })
        : setFilters({ publicationStatus: undefined });
    }
    function setOwners(ownerIds: string[]) {
      ownerIds.length
        ? setFilters({ ownerId: ownerIds })
        : setFilters({ ownerId: undefined });
    }
    function setCollections(collectionIds: string[]) {
      if (!collectionIds.length) {
        setFilters({ collectionId: undefined, excludeOrphans: undefined });
        return;
      }
      const excludeOrphans = collectionIds.find((id) => id === noCollectionsId)
        ? false
        : true;

      setFilters({
        collectionId: collectionIds.filter((id) => id !== noCollectionsId),
        excludeOrphans:
          typeof excludeOrphans === 'boolean'
            ? excludeOrphans.toString()
            : undefined,
      });
    }
    function setLanguageId(langId: string[]) {
      setFilters({ langIdFilter: langId });
    }

    return {
      setSearchInput,
      setPublicationStatuses,
      setOwners,
      setCollections,
      setLanguageId,
    };
  }, [setFilters]);

  const safeStore: AppFilterStore = useMemo(
    () => ({
      search: untypedStore.search,
      publicationStatus: typeSafePublicationStatusKeys(
        untypedStore.publicationStatus,
      ),
      collectionId: filterNonNullStrings(untypedStore.collectionId),
      ownerId: filterNonNullStrings(untypedStore.ownerId),
      excludeOrphans:
        typeof untypedStore.excludeOrphans === 'string'
          ? untypedStore.excludeOrphans
          : undefined,
      langId: untypedStore.langIdFilter?.length
        ? (untypedStore.langIdFilter as LanguageId[])
        : [defaultLanguageId],
    }),
    [
      untypedStore.collectionId,
      untypedStore.search,
      untypedStore.publicationStatus,
      untypedStore.ownerId,
      untypedStore.excludeOrphans,
      untypedStore.langIdFilter,
      defaultLanguageId,
    ],
  );

  const query = useMemo(() => getArticleQuery(safeStore), [safeStore]);
  const filters = useMemo(() => getArticleFilters(safeStore), [safeStore]);

  const articlesQueryLanguageId =
    selectedLanguage.languageId ?? defaultLanguageId;

  const articlesQueryVariables: ArticlesQueryVariables = useMemo(
    () => ({
      offset,
      limit,
      languageId: articlesQueryLanguageId,
      query,
      filters: filters && Object.keys(filters).length ? filters : undefined,
    }),
    [articlesQueryLanguageId, filters, limit, offset, query],
  );

  return { store: safeStore, actions, articlesQueryVariables };
}

/** Type safe publicationStatusKeys */
function typeSafePublicationStatusKeys(
  statuses: (string | null)[] | null | undefined,
): PublicationStatusKey[] {
  if (!statuses || !statuses.length) return [];
  return statuses as PublicationStatusKey[];
}

function getArticleQuery(
  store: AppFilterStore,
): InputMaybe<ArticleSearchInput> | undefined {
  const searchLanguage = store.langId?.at(0);
  const searchTerms = store.search;

  return searchLanguage && searchTerms
    ? {
        searchLanguage,
        searchTerms,
      }
    : undefined;
}
